/* Oreganix site-wide helpers: sticky header-group bars, load more / infinite scroll, content protection.
   No external requests. */

(() => {
  /* ---------- Sticky bars (announcement bar, ticker) ---------- */
  const stickyBars = new Set();
  let stickyFrame = 0;

  const layoutStickyBars = () => {
    stickyFrame = 0;
    const sections = [...stickyBars]
      .map((bar) => bar.closest('.shopify-section'))
      .filter(Boolean)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    let offset = 0;
    sections.forEach((section) => {
      if (getComputedStyle(section).position !== 'sticky') {
        section.style.removeProperty('top');
        return;
      }
      section.style.top = `${offset}px`;
      offset += section.offsetHeight;
    });
    document.documentElement.style.setProperty('--ox-sticky-bars-height', `${offset}px`);
  };
  const queueStickyLayout = () => {
    if (!stickyFrame) stickyFrame = requestAnimationFrame(layoutStickyBars);
  };
  const stickyResize = 'ResizeObserver' in window ? new ResizeObserver(queueStickyLayout) : null;
  window.addEventListener('resize', queueStickyLayout);

  if (!customElements.get('ox-sticky-bar')) {
    customElements.define(
      'ox-sticky-bar',
      class OxStickyBar extends HTMLElement {
        connectedCallback() {
          this.section = this.closest('.shopify-section');
          stickyBars.add(this);
          if (stickyResize && this.section) stickyResize.observe(this.section);
          queueStickyLayout();
        }

        disconnectedCallback() {
          stickyBars.delete(this);
          if (stickyResize && this.section) stickyResize.unobserve(this.section);
          if (this.section) this.section.style.removeProperty('top');
          queueStickyLayout();
        }
      }
    );
  }

  /* ---------- Load more / infinite scroll (collection and search grids) ---------- */
  if (!customElements.get('ox-load-more')) {
    customElements.define(
      'ox-load-more',
      class OxLoadMore extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('.ox-load-more__button');
          if (!this.button) return;
          this.button.addEventListener('click', (event) => {
            event.preventDefault();
            this.load();
          });
          if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
            this.button.classList.add('is-auto');
            this.observer = new IntersectionObserver(
              (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) this.load();
              },
              { rootMargin: '0px 0px 600px 0px' }
            );
            this.observer.observe(this);
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        async load() {
          if (this.hasAttribute('loading') || !this.button) return;
          const grid = document.getElementById('product-grid');
          if (!grid) return;
          this.setAttribute('loading', '');
          try {
            const url = new URL(this.button.href, window.location.origin);
            url.searchParams.set('section_id', this.dataset.sectionId);
            const response = await fetch(url.toString());
            if (!response.ok) throw new Error(response.status);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const nextGrid = doc.getElementById('product-grid');
            const items = nextGrid ? [...nextGrid.children] : [];
            const fragment = document.createDocumentFragment();
            items.forEach((item) => fragment.appendChild(document.importNode(item, true)));
            const firstNew = fragment.firstElementChild;
            grid.appendChild(fragment);
            if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(grid);

            const nextLoader = doc.querySelector('ox-load-more');
            const nextButton = nextLoader && nextLoader.querySelector('.ox-load-more__button');
            const shown = nextLoader && nextLoader.querySelector('[data-ox-shown]');
            if (nextButton) {
              this.button.href = nextButton.getAttribute('href');
              const current = this.querySelector('[data-ox-shown]');
              if (current && shown) current.textContent = shown.textContent;
              this.removeAttribute('loading');
              if (this.observer) {
                // Re-check in case the loader is still in view after appending a short page.
                this.observer.unobserve(this);
                this.observer.observe(this);
              }
            } else {
              this.remove();
            }
            if (firstNew && this.dataset.mode === 'button') {
              const link = firstNew.querySelector('a[href]');
              if (link) link.focus({ preventScroll: true });
            }
          } catch (error) {
            // Fall back to a normal page load so visitors are never stuck.
            window.location.href = this.button.href;
          }
        }
      }
    );
  }

  /* ---------- Content protection ---------- */
  const protect = (document.body && document.body.dataset.oxProtect ? document.body.dataset.oxProtect : '')
    .split(' ')
    .filter(Boolean);
  if (protect.length && !(window.Shopify && window.Shopify.designMode)) {
    const isField = (el) => el && el.closest && el.closest('input, textarea, select, [contenteditable="true"]');
    const isMedia = (el) => el && el.closest && el.closest('img, picture, video, svg, canvas, .media, [style*="background-image"]');

    if (protect.includes('all-click') || protect.includes('images-click')) {
      document.addEventListener('contextmenu', (event) => {
        if (isField(event.target)) return;
        if (protect.includes('all-click') || isMedia(event.target)) event.preventDefault();
      });
    }
    if (protect.includes('drag') || protect.includes('images-click') || protect.includes('all-click')) {
      document.addEventListener('dragstart', (event) => {
        if (event.target && event.target.tagName === 'IMG') event.preventDefault();
      });
    }
    if (protect.includes('copy')) {
      ['copy', 'cut'].forEach((type) =>
        document.addEventListener(type, (event) => {
          if (!isField(event.target)) event.preventDefault();
        })
      );
      document.addEventListener('selectstart', (event) => {
        if (!isField(event.target)) event.preventDefault();
      });
    }
    if (protect.includes('keys')) {
      document.addEventListener('keydown', (event) => {
        if (!(event.ctrlKey || event.metaKey)) return;
        const key = event.key.toLowerCase();
        if (key === 's' || key === 'u' || key === 'p') event.preventDefault();
      });
    }
  }
})();
